import toArray from "lodash/toArray";

import { useTranslator } from "@eo-locale/react";

import { CommonLikertQuestion } from "@/storybook-components";
import type { Question } from "@/types";
import { setSurveyAnswers } from "@/redux/features/survey/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

interface LikertQuestionProps {
  question: Question;
}

const LikertQuestion = ({ question }: LikertQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, statements, saving } = survey;

  const error = errors[question.id] || {};
  const answer = answers[question.id] || {};

  const sortedStatements = toArray(statements)
    .filter((ls) => ls.parentId === parseInt(question.id))
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  const sortedStatementOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  const handleChange = (value: string, statementId: string | number) => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          ...answers[question.id],
          [statementId]: parseInt(value)
        }
      })
    );
  };

  return (
    <CommonLikertQuestion
      layoutProps={{ width: "100%", as: "fieldset" }}
      isInvalid={!!error.code}
      isRequired={question.required}
      isDisabled={saving}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      questionId={question?.id}
      questionLabelType="legend"
      answer={answer}
      statements={sortedStatements}
      options={sortedStatementOptions}
      contentLayoutProps={{ spacing: spacing.answerStack, py: 4 }}
      answerGroupSpacing={spacing.answerGroup}
      answerLabelProps={{
        ...textStyles?.contentLabel,
        className: "ehq-surveyLikertStatement"
      }}
      size="xlIconWithLabelStyle"
      handleChange={handleChange}
      answerBlockSpacing={spacing.answerBlock}
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default LikertQuestion;
